import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { WebsocketEvent } from 'entities/WebSocket'
import { User } from 'entities/Users/model/User'
import {
  type IParamsOrganizationMembers,
  type IResponseEventAwayStatusChanged,
  type IResponseUser,
} from 'entities/Users/api/types'
import { RowUser } from 'entities/Users/model/Table/RowUser'
import { UsersApi } from 'entities/Users'
import { tableLimitStore } from 'entities/TableLimit'

export class ActiveMembersStore {
  private readonly _tableStore: TableStore<RowUser>
  private _itemsMap: Map<number, User> = new Map()
  private _search = ''
  private _scrollToTopTrigger = ''
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeAwayStatus: (() => void) | null = null

  constructor(private _entities: IEntitiesStore) {
    this._tableStore = new TableStore({
      key: 'activeMembersTable',
      element: 'member',
      limit: 10,
      page: 1,
      getLimit: () => tableLimitStore.getLimit('active-members-table'),
      setLimit: (value) => tableLimitStore.setLimit('active-members-table', value),
      useColumnsCache: true,
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    this.subscribeAwayStatus()
  }

  subscribeAwayStatus = () => {
    this._disposeAwayStatus?.()
    this._disposeAwayStatus = this._entities.websocket.on(
      WebsocketEvent.UserAwayStatusChanged,
      this._handleAwayStatusChanged
    )
  }

  private _handleAwayStatusChanged = (data: IResponseEventAwayStatusChanged) => {
    this._itemsMap.get(data.id)?.setAwayStatus(data.isAway)
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _setItem = (data: IResponseUser) => {
    runInAction(() => {
      this._itemsMap.set(data.id, new User(data))
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      this._tableStore.setBulkTotal(0)
      this._tableStore.bulkStore.resetBulkAll()

      try {
        const [{ data }] = await Promise.all([
          UsersApi.getOrganizationActiveMembersList(this.paramsGetItems, {
            cancelToken: cancelRequest.token,
          }),
          this._entities.inboxesStore.organizationInboxesInitPromise,
          this._entities.permissionsStore.init(),
        ])

        runInAction(() => {
          this._itemsMap.clear()
          data.data.forEach((item) => {
            this._setItem(item)
          })
          this._tableStore.setRows(this.itemsRows)
          this._tableStore.setTotal(data.meta.total)
          this._scrollToTopTrigger = nanoid()
        })
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 408) {
          logger.warn(
            'TABLE: active-members-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const newLimit = tableLimitStore.reduceLimit(
            'active-members-table',
            this._tableStore.limit
          )
          this._tableStore.setLimit(newLimit)
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    },
  })

  loadData = async () => {
    await this._requestLoadData.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get paramsWithoutPage(): Omit<IParamsOrganizationMembers, 'page'> {
    return {
      per_page: this._tableStore.limit,
      search: this._search || undefined,
      sortBy: this._tableStore.sortBy || undefined,
      sortOrder: this._tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IParamsOrganizationMembers {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get itemsRows() {
    return this.items.map((item) => new RowUser(item, this._entities))
  }

  get tableStore() {
    return this._tableStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get search() {
    return this._search
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._disposeAwayStatus?.()
    this._requestLoadData.dispose()
  }
}
